import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck,
  UserCheck,
  Wrench,
  Droplet,
  FileText,
  Shield,
  ScrollText,
  ClipboardCheck,
  Receipt,
  Gauge,
  AlertOctagon,
  History,
  ChevronRight,
  Home,
} from 'lucide-react';
import MainService from '../../services/MainService';
import { normalizeVehicleGridResponse } from '../../utils/vehicleApiUtils';

const QUICK_LINKS = [
  { path: '/home/vehicle-management/vehicle-list', label: 'Vehicle list', desc: 'Fleet register', icon: Truck },
  { path: '/home/vehicle-management/assignments', label: 'Assignments', desc: 'Drivers & employees', icon: UserCheck },
  { path: '/home/vehicle-management/maintenance', label: 'Maintenance', desc: 'Work & schedules', icon: Wrench },
  { path: '/home/vehicle-management/fuel-logs', label: 'Fuel logs', desc: 'Fill-ups & cost', icon: Droplet },
  { path: '/home/vehicle-management/documents', label: 'Documents', desc: 'Files & compliance', icon: FileText },
  { path: '/home/vehicle-management/insurance', label: 'Insurance', desc: 'Policies', icon: Shield },
  { path: '/home/vehicle-management/permits', label: 'Permits', desc: 'Permits & renewals', icon: ScrollText },
  { path: '/home/vehicle-management/inspections', label: 'Inspections', desc: 'Safety checks', icon: ClipboardCheck },
  { path: '/home/vehicle-management/vehicle-expenses', label: 'Vehicle expenses', desc: 'Linked to expense master', icon: Receipt },
  { path: '/home/vehicle-management/odometer-logs', label: 'Odometer logs', desc: 'Mileage history', icon: Gauge },
  { path: '/home/vehicle-management/accidents', label: 'Accidents', desc: 'Incidents', icon: AlertOctagon },
  { path: '/home/vehicle-management/service-history', label: 'Service history', desc: 'Workshop visits', icon: History },
  { path: '/home/vehicle-management/fine-records', label: 'Fine records', desc: 'Traffic & penalties', icon: FileText },
];

export default function VehicleDashboard() {
  const navigate = useNavigate();
  const [fleetTotal, setFleetTotal] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await MainService.VehiclesInGrid({ Page: 1, PageSize: 1, Keyword: '' });
        const { totalCount } = normalizeVehicleGridResponse(res);
        if (!cancelled) {
          setFleetTotal(totalCount);
          setLoadError(false);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setFleetTotal(null);
          setLoadError(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-full bg-gradient-to-br from-slate-50 via-emerald-50/40 to-slate-100">
      <div className="mx-auto max-w-6xl space-y-8 px-4 py-8 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-medium uppercase tracking-wide text-[#46955A]">Dashboards</p>
            <h1 className="mt-1 text-3xl font-bold text-slate-900">Vehicle dashboard</h1>
            <p className="mt-2 max-w-xl text-slate-600">
              Overview and shortcuts to fleet operations. Open a module to record or review data.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="inline-flex items-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <Home className="h-4 w-4" />
            Main dashboard
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fleet</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">
              {fleetTotal === null && !loadError ? '—' : loadError ? '—' : fleetTotal}
            </p>
            <p className="mt-1 text-sm text-slate-600">Vehicles in register</p>
            {loadError && (
              <p className="mt-2 text-xs text-amber-700">Could not load count. Open vehicle list to verify.</p>
            )}
          </div>
          <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:col-span-1 lg:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tip</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Use <span className="font-medium text-slate-800">Vehicle management</span> in the sidebar for
              the full menu. This page is a hub for daily fleet work.
            </p>
          </div>
        </div>

        <div>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">Quick links</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {QUICK_LINKS.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className="group flex w-full items-start gap-3 rounded-2xl border border-slate-200/90 bg-white p-4 text-left shadow-sm transition hover:border-[#46955A]/40 hover:shadow-md"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#46955A]/10 text-[#46955A]">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-slate-900">{item.label}</span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-[#46955A]" />
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500">{item.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
